import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLottery } from "@/hooks/use-lottery-selection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";

interface PrizeTier {
  faixa: number;
  quantidade_acertos?: string;
  descricao?: string;
  numero_ganhadores: number;
  valor_premio: number;
}

interface DrawRow {
  id: string;
  contest_number: number;
  draw_date: string | null;
  accumulated: boolean | null;
  accumulated_amount: number | null;
  prize_pool: number | null;
  winners_6: number | null;
  numbers: number[];
  numbers2: number[];
  prize_tiers: PrizeTier[] | null;
}

const PAGE_SIZE = 20;

const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const style = getComputedStyle(node);
    const overflowY = style.overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight + 4) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const animateScroll = (
  container: HTMLElement | null,
  to: number,
  duration = 900,
  onDone?: () => void,
) => {
  const getTop = () => (container ? container.scrollTop : window.scrollY);
  const setTop = (v: number) => {
    if (container) container.scrollTop = v;
    else window.scrollTo(0, v);
  };
  const from = getTop();
  const delta = to - from;
  if (Math.abs(delta) < 2) { setTop(to); onDone?.(); return; }
  const start = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / duration);
    setTop(from + delta * easeInOutCubic(t));
    if (t < 1) requestAnimationFrame(step);
    else onDone?.();
  };
  requestAnimationFrame(step);
};

const formatBRL = (n: number | null | undefined) =>
  typeof n === "number" && isFinite(n) && n > 0
    ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

const prizeTierHits = (lotterySlug: string, tier: PrizeTier): string => {
  if (tier.quantidade_acertos) return tier.quantidade_acertos;
  if (tier.descricao) return tier.descricao;

  const hitsByTier: Record<string, number[]> = {
    "mega-sena": [6, 5, 4],
    lotofacil: [15, 14, 13, 12, 11],
    quina: [5, 4, 3, 2],
    "dupla-sena": [6, 5, 4, 3, 6, 5, 4, 3],
  };
  const hits = hitsByTier[lotterySlug]?.[tier.faixa - 1];
  return hits ? `${hits} acertos` : `${tier.faixa} acertos`;
};

const Historico = () => {
  const { config } = useLottery();
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<DrawRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchContest, setSearchContest] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [inverted, setInverted] = useState(false);
  const [pendingScroll, setPendingScroll] = useState<"up" | "down" | null>(null);
  const [loadedPage, setLoadedPage] = useState(0);
  const pagingRef = useRef(false);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setPage(0); setSearchContest(null); setSearchTerm(""); setExpandedId(null); }, [config.slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pagingRef.current) setLoading(true);
      const { data: lottery } = await supabase
        .from("lotteries").select("id").eq("slug", config.slug).single();
      if (!lottery) { setLoading(false); return; }

      let countQ = supabase
        .from("draws").select("*", { count: "exact", head: true }).eq("lottery_id", lottery.id);
      if (searchContest) countQ = countQ.eq("contest_number", searchContest);
      const { count } = await countQ;
      if (cancelled) return;
      setTotal(count ?? 0);

      let q = supabase
        .from("draws")
        .select("id, contest_number, draw_date, accumulated, accumulated_amount, prize_pool, winners_6, prize_tiers")
        .eq("lottery_id", lottery.id)
        .order("contest_number", { ascending: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
      if (searchContest) q = q.eq("contest_number", searchContest);

      const { data: draws } = await q;
      if (!draws || cancelled) { setRows([]); setLoading(false); return; }

      const ids = draws.map((d) => d.id);
      const { data: nums } = await supabase
        .from("draw_numbers")
        .select("draw_id, number, position")
        .in("draw_id", ids)
        .order("number", { ascending: true });

      const grouped: Record<string, number[]> = {};
      const grouped2: Record<string, number[]> = {};
      (nums ?? []).forEach((n) => {
        const id = (n as { draw_id: string }).draw_id;
        const pos = (n as { position: number }).position;
        if (pos > 100) {
          (grouped2[id] ??= []).push((n as { number: number }).number);
        } else {
          (grouped[id] ??= []).push((n as { number: number }).number);
        }
      });

      if (cancelled) return;
      setRows(draws.map((d) => ({
        ...d,
        numbers: (grouped[d.id] ?? []).sort((a, b) => a - b),
        numbers2: (grouped2[d.id] ?? []).sort((a, b) => a - b),
        prize_tiers: Array.isArray((d as { prize_tiers?: unknown }).prize_tiers)
          ? ((d as unknown as { prize_tiers: PrizeTier[] }).prize_tiers)
          : null,
      })));
      setLoadedPage(page);
      setLoading(false);
    })();
  return () => { cancelled = true; };
  }, [config.slug, page, searchContest]);

  useEffect(() => {
    if (pendingScroll || !inverted || !bottomRef.current || loading || rows.length === 0) return;
    bottomRef.current.scrollIntoView({ behavior: "auto", block: "end" });
  }, [inverted, loading, rows, page]);

  useEffect(() => {
    if (!pendingScroll || loading || rows.length === 0 || loadedPage !== page) return;
    const container = getScrollParent(topRef.current);
    const viewportH = container ? container.clientHeight : window.innerHeight;
    const currentTop = container ? container.scrollTop : window.scrollY;
    const containerTop = container ? container.getBoundingClientRect().top : 0;

    const topTarget = topRef.current
      ? currentTop + (topRef.current.getBoundingClientRect().top - containerTop) - 12
      : 0;
    const bottomTarget = bottomRef.current
      ? currentTop + (bottomRef.current.getBoundingClientRect().bottom - containerTop) - viewportH + 80
      : topTarget;

    // Parada final: sempre no topo (modo recente em cima) ou na base (modo recente embaixo).
    const target = inverted ? bottomTarget : topTarget;
    // Entrada na borda oposta para que a rolagem seja perceptível na direção pedida.
    // "up" = scrollTop aumenta (tela sobe), "down" = scrollTop diminui (tela desce).
    const travel = Math.max(240, viewportH * 0.9);
    const entry = pendingScroll === "up" ? target - travel : target + travel;

    // Posiciona instantaneamente na borda oposta e depois desliza até o destino,
    // para que o usuário perceba o movimento (passado ↓ / futuro ↑).
    if (container) container.scrollTop = Math.max(0, entry);
    else window.scrollTo(0, Math.max(0, entry));

    const id = requestAnimationFrame(() => {
      animateScroll(container, Math.max(0, target), 900, () => {
        pagingRef.current = false;
      });
    });
    setPendingScroll(null);
    return () => cancelAnimationFrame(id);
  }, [pendingScroll, loading, rows, loadedPage, page, inverted]);

  const goOlder = () => {
    pagingRef.current = true;
    setPage((p) => Math.min(totalPages - 1, p + 1));
    setPendingScroll(inverted ? "down" : "up");
  };

  const goNewer = () => {
    pagingRef.current = true;
    setPage((p) => Math.max(0, p - 1));
    setPendingScroll(inverted ? "up" : "down");
  };

  const handleSearch = () => {
    const n = parseInt(searchTerm.trim(), 10);
    setSearchContest(Number.isFinite(n) && n > 0 ? n : null);
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const displayRows = inverted ? [...rows].reverse() : rows;

  const pager = !searchContest && totalPages > 1 ? (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">
        Página {page + 1} de {totalPages}
      </span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={goOlder} disabled={page >= totalPages - 1}>
          <ChevronLeft className="h-4 w-4" /> Anterior
        </Button>
        <Button size="sm" variant="outline" onClick={goNewer} disabled={page === 0}>
          Posterior <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-foreground">Histórico — {config.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {total.toLocaleString("pt-BR")} concursos no banco. Importação completa em andamento.
        </p>
      </div>

      <div ref={topRef} className="h-0" />
      <Card className="border-lt-border bg-card">
        <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle className="font-serif text-xl">Concursos</CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 rounded-md border border-lt-border px-3 py-1.5">
              <Switch
                id="inverted-order"
                checked={inverted}
                onCheckedChange={setInverted}
                className="data-[state=checked]:bg-primary"
              />
              <Label htmlFor="inverted-order" className="cursor-pointer text-xs text-muted-foreground">
                Mais recente embaixo
              </Label>
            </div>
            <Input
              placeholder="Buscar concurso nº…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="h-9 w-44 border-lt-border bg-background"
            />
            <Button size="sm" variant="outline" onClick={handleSearch}>
              <Search className="h-3.5 w-3.5" />
            </Button>
            {searchContest && (
              <Button size="sm" variant="ghost" onClick={() => { setSearchContest(null); setSearchTerm(""); setPage(0); }}>
                Limpar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pager && <div className="mb-4">{pager}</div>}
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Nenhum concurso encontrado.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-lt-border">
                  <TableHead className="w-8"></TableHead>
                  <TableHead className="w-20">Concurso</TableHead>
                  <TableHead className="w-28">Data</TableHead>
                  <TableHead>Dezenas</TableHead>
                  <TableHead className="w-24 text-right">Ganhadores</TableHead>
                  <TableHead className="w-20 text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRows.map((r) => {
                  const isOpen = expandedId === r.id;
                  const tiers = (r.prize_tiers ?? []).slice().sort((a, b) => a.faixa - b.faixa);
                  return (
                    <>
                      <TableRow
                        key={r.id}
                        className="cursor-pointer border-lt-border hover:bg-muted/30"
                        onClick={() => setExpandedId(isOpen ? null : r.id)}
                      >
                        <TableCell className="text-muted-foreground">
                          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                        </TableCell>
                        <TableCell className="font-mono text-foreground">{r.contest_number}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {r.draw_date ? new Date(r.draw_date + "T12:00:00").toLocaleDateString("pt-BR") : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1.5">
                            <div className="flex flex-wrap items-center gap-1">
                              {r.numbers2.length > 0 && (
                                <span className="mr-1 text-[10px] uppercase tracking-wider text-muted-foreground">1º</span>
                              )}
                              {r.numbers.map((n) => (
                                <span key={`a-${n}`} className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-lt-gold-border bg-lt-gold-bg font-mono text-xs text-foreground">
                                  {String(n).padStart(2, "0")}
                                </span>
                              ))}
                            </div>
                            {r.numbers2.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1">
                                <span className="mr-1 text-[10px] uppercase tracking-wider text-muted-foreground">2º</span>
                                {r.numbers2.map((n) => (
                                  <span key={`b-${n}`} className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-lt-teal-border bg-lt-teal-bg font-mono text-xs text-foreground">
                                    {String(n).padStart(2, "0")}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-foreground">
                          {r.winners_6 ?? 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.accumulated ? (
                            <span className="text-xs text-accent">Acumulou</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Premiado</span>
                          )}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow key={r.id + "-detail"} className="border-lt-border bg-muted/10 hover:bg-muted/10">
                          <TableCell colSpan={6} className="py-4">
                            <div className="space-y-3">
                              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                                <span className="text-muted-foreground">
                                  Arrecadação total:{" "}
                                  <span className="font-mono text-foreground">{formatBRL(r.prize_pool)}</span>
                                </span>
                                {r.accumulated && (
                                  <span className="text-muted-foreground">
                                    Valor acumulado:{" "}
                                    <span className="font-mono text-foreground">{formatBRL(r.accumulated_amount)}</span>
                                  </span>
                                )}
                              </div>
                              {tiers.length === 0 ? (
                                <p className="text-xs text-muted-foreground">
                                  Detalhamento de faixas não disponível para este concurso.
                                </p>
                              ) : (
                                <div className="overflow-x-auto rounded-md border border-lt-border">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="border-lt-border">
                                        <TableHead className="h-9 text-xs">Faixa</TableHead>
                                        <TableHead className="h-9 text-right text-xs">Ganhadores</TableHead>
                                        <TableHead className="h-9 text-right text-xs">Prêmio individual</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {tiers.map((t) => (
                                        <TableRow key={t.faixa} className="border-lt-border">
                                          <TableCell className="py-2 text-foreground">
                                            {prizeTierHits(config.slug, t)}
                                          </TableCell>
                                          <TableCell className="py-2 text-right font-mono text-foreground">
                                            {(t.numero_ganhadores ?? 0).toLocaleString("pt-BR")}
                                          </TableCell>
                                          <TableCell className="py-2 text-right font-mono text-foreground">
                                            {formatBRL(t.valor_premio)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <div ref={bottomRef} className="h-0" />

          {pager && <div className="mt-4">{pager}</div>}
        </CardContent>
      </Card>
    </div>
  );
};

export default Historico;
